import { useTranslation } from 'react-i18next'
import { useAssistant } from '@/shell/AppShell'
import { ForgeButton } from '@/components/forge/ForgeButton'
import { Icon } from '@/components/forge/Icon'
import { requestedAction } from '@/features/copilot/turnMetadata'

/**
 * The ForgeAI buttons: one word and the ForgeAI mark, which is what says the
 * press goes to Forge AI rather than opening the screen's own form.
 *
 * Create sits on a list, Update and Investigate on a record. A press opens the dock
 * and sends the fixed sentence with one requested action; see `request` in the
 * shell. The pair is the server's whole vocabulary — anything else is a 400 — so
 * the buttons are a closed table rather than a `type` and `module` a caller could
 * combine freely. An anomaly takes Investigate until it has an analysis, then
 * Update.
 */
const ACTIONS = {
  investigateAnomaly: { type: 'request_rca', module: 'anomalies' },
  updateAnomaly: { type: 'update', module: 'anomalies' },
  createWorkOrder: { type: 'create', module: 'work_orders' },
  updateWorkOrder: { type: 'update', module: 'work_orders' },
  createLogbookEntry: { type: 'create', module: 'logbook' },
  updateLogbookEntry: { type: 'update', module: 'logbook' },
  createShiftHandover: { type: 'create', module: 'shift_handovers' },
  updateShiftHandover: { type: 'update', module: 'shift_handovers' },
}

/**
 * @param {keyof typeof ACTIONS} props.action
 * @param {string} [props.recordId] the record on screen; required for all but a create
 * @param {string} [props.recordLabel] its serial, written as `#WO-12` at the start of
 *   the message. Without one the message starts with the page's own chip.
 * @param {() => void} [props.onAsk] runs after the request is sent — a dialog closes
 *   itself here so the dock it opened is not behind the overlay
 */
export function ForgeAIButton({ action, recordId, recordLabel, onAsk, disabled, ...rest }) {
  const { t } = useTranslation('app')
  const { request, busy } = useAssistant()
  const { type, module } = ACTIONS[action]

  /* Static keys, so the i18n check can see every one. */
  const copy = {
    investigateAnomaly: [t('dock.forgeActions.investigateAnomaly.label'), t('dock.forgeActions.investigateAnomaly.message')],
    updateAnomaly: [t('dock.forgeActions.updateAnomaly.label'), t('dock.forgeActions.updateAnomaly.message')],
    createWorkOrder: [t('dock.forgeActions.createWorkOrder.label'), t('dock.forgeActions.createWorkOrder.message')],
    updateWorkOrder: [t('dock.forgeActions.updateWorkOrder.label'), t('dock.forgeActions.updateWorkOrder.message')],
    createLogbookEntry: [t('dock.forgeActions.createLogbookEntry.label'), t('dock.forgeActions.createLogbookEntry.message')],
    updateLogbookEntry: [t('dock.forgeActions.updateLogbookEntry.label'), t('dock.forgeActions.updateLogbookEntry.message')],
    createShiftHandover: [t('dock.forgeActions.createShiftHandover.label'), t('dock.forgeActions.createShiftHandover.message')],
    updateShiftHandover: [t('dock.forgeActions.updateShiftHandover.label'), t('dock.forgeActions.updateShiftHandover.message')],
  }
  const [label, message] = copy[action]

  return (
    <ForgeButton
      variant="ai"
      title={message}
      /* The assistant takes one question at a time and there is no queue. A
         record action waits for the record's id. */
      disabled={disabled || busy || (type !== 'create' && !recordId)}
      onClick={() => {
        request({
          message,
          action: requestedAction(type, module, recordId ?? null),
          ...(recordLabel ? { chip: `#${recordLabel}` } : {}),
        })
        onAsk?.()
      }}
      {...rest}
    >
      <Icon name="ai" size="sm" />
      {label}
    </ForgeButton>
  )
}
